import type { Ref } from "vue";

interface LocalisedPage {
  title?: string;
  seo_title?: string;
  description?: string;
  meta_description?: string;
  hero_image?: string;
  hero_image_alt?: string;
  og_image?: string;
}

const SITE_URL = "https://georgiadream.ge";
const FALLBACK_OG_IMAGE = `${SITE_URL}/images/og/home.jpg`;

const LOCALES = ["en", "ru", "ka"] as const;
type Locale = (typeof LOCALES)[number];

// Match the `language` values in nuxt.config.ts i18n.locales so useHead can
// dedupe identical links emitted by @nuxtjs/i18n. Language-only codes (no
// region) on purpose — see the comment on i18n.locales in nuxt.config.ts.
// `x-default` falls outside that scheme and is added separately below.
const HREFLANG: Record<Locale, string> = {
  en: "en",
  ru: "ru",
  ka: "ka",
};

const OG_LOCALE: Record<Locale, string> = {
  en: "en_US",
  ru: "ru_RU",
  ka: "ka_GE",
};

function stripLocalePrefix(path: string): string {
  return path.replace(/^\/(ru|ka)(?=\/|$)/, "") || "/";
}

function buildLocaleUrl(locale: Locale, cleanPath: string): string {
  if (locale === "en") return `${SITE_URL}${cleanPath}`;
  const base = cleanPath === "/" ? "" : cleanPath;
  return `${SITE_URL}/${locale}${base}`;
}

export function useLocalisedSeo(page: Ref<LocalisedPage | null | undefined>) {
  const { locale } = useI18n();
  const route = useRoute();

  const canonicalUrl = computed(() => `${SITE_URL}${route.path}`);

  const cleanPath = computed(() => stripLocalePrefix(route.path));

  // hreflang tells Google which page to serve per locale. x-default is the
  // fallback for locales we don't ship (defaults to English).
  const alternateLinks = computed(() => {
    const links = LOCALES.map((l) => ({
      rel: "alternate" as const,
      hreflang: HREFLANG[l],
      href: buildLocaleUrl(l, cleanPath.value),
    }));
    links.push({
      rel: "alternate" as const,
      hreflang: "x-default",
      href: buildLocaleUrl("en", cleanPath.value),
    });
    return links;
  });

  const ogLocale = computed(() => OG_LOCALE[locale.value as Locale] ?? OG_LOCALE.en);

  const ogLocaleAlternate = computed(() =>
    LOCALES.filter((l) => l !== locale.value).map((l) => OG_LOCALE[l])
  );

  const ogImage = computed(() => {
    // Prefer a page-specific 1200×630 OG variant; fall back to hero then site default.
    const src = page.value?.og_image || page.value?.hero_image;
    if (!src) return FALLBACK_OG_IMAGE;
    return src.startsWith("http") ? src : `${SITE_URL}${src}`;
  });

  const seoTitle = () => page.value?.seo_title || page.value?.title;
  const seoDescription = () =>
    page.value?.meta_description || page.value?.description;
  const ogImageAlt = () =>
    page.value?.hero_image_alt || page.value?.title || "Dreamland Oasis Chakvi";

  useHead({
    // Content-driven pages embed the full brand suffix in their `seo_title`
    // (e.g. "Pools, Aquapark & Beach · Dreamland Oasis Chakvi"); skip the
    // default layout suffix to keep the SERP title under ~60 characters.
    titleTemplate: "%s",
    link: () => [
      { rel: "canonical", href: canonicalUrl.value },
      ...alternateLinks.value,
    ],
  });

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogType: "website",
    ogUrl: canonicalUrl,
    ogImage,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogImageAlt,
    ogSiteName: "Dreamland Oasis Chakvi",
    ogLocale,
    ogLocaleAlternate,
    twitterCard: "summary_large_image",
    twitterTitle: seoTitle,
    twitterDescription: seoDescription,
    twitterImage: ogImage,
    twitterImageAlt: ogImageAlt,
  });
}
